import dayjs from "dayjs";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";

import {
  useCompanyScopeContext,
  useHubRideActionConfirm,
  useRideDtoActions,
  useRideEmptyStationsAllowed,
  useRidesActions,
  useRideStationContext,
} from "@ryde/redux";
import {
  addMissingTimesStationsInfo,
  convertUTCToISO,
  dateTimeWithoutSeconds,
  DirectionEnum,
  formatDatetime,
  getIsUpdatedStationTimeValid,
  getRealRouteEndpoints,
  GlobalAppEventEnum,
  HubRideActionKind,
  isUpdatedStationTimeInsideShiftLimits,
  KeyboardKeys,
  Log,
  MIN_DATE_ISO,
  normalizeRideMetadata,
  RideStationPanelMode,
  simpleResultErrorInfo,
  SINGLE_KEY,
  timeDiffMinutesSigned,
  updateMetadataStationsTime,
  useFormats,
  useSystemParams,
  useTimezone,
  warnAndToastSimpleResult,
} from "@ryde/shared";

import { adjustTimeForMidnightCrossingRide } from "./ride-station-panel.utils";

const UPDATED_STATION_TIME_VALIDATION_ERROR_LIFETIME = 10_000;

export const useRideStationTimeEdit = () => {
  const { t } = useTranslation();
  const { allowShiftTimeOverrideDuringManualAction } = useSystemParams();
  const { station, rideDto, mode } = useRideStationContext();
  const { isRideHasIndependentDayAdjustment } = useRideEmptyStationsAllowed(
    mode,
    rideDto,
  );
  const { arrivalTime } = station || {};
  const { timeFormat } = useFormats();
  const { timezone } = useTimezone();
  const { updateEditingRideMetadataRideDate, updateEditingRideMetadata } =
    useRideDtoActions();
  const { updateRideStartEndDate } = useRidesActions();
  const { confirmHubRideAction } = useHubRideActionConfirm();
  const { eventEmitter } = useCompanyScopeContext();

  const initialValue = arrivalTime ? arrivalTime : undefined;
  const refValue = useRef<TDateISO | undefined>(initialValue);

  const [value, setValue] = useState<TDateISO | undefined>(initialValue);
  const [error, setError] = useState<null | Error>(null);

  const hasOverlappingDates = useMemo(() => {
    if (!value || !rideDto?.rideStart || !rideDto?.rideEnd) {
      return false;
    }

    if (
      dayjs(value).isBefore(dayjs(rideDto.rideStart)) ||
      dayjs(value).isAfter(dayjs(rideDto.rideEnd))
    ) {
      return true;
    }

    return false;
  }, [rideDto?.rideEnd, rideDto?.rideStart, value]);

  useEffect(() => {
    if (hasOverlappingDates) {
      Log.debug(
        `Station has overlapping dates with ride start/end, ${formatDatetime({ dateTimeISO: value, timezone })} `,
      );
    }
  }, [hasOverlappingDates, value, timezone]);

  useEffect(() => {
    if (initialValue !== refValue.current) {
      setValue(initialValue);
      refValue.current = initialValue;
    }
  }, [initialValue]);

  const handleTimeInputChange = useEvent((newValueUTC: dayjs.Dayjs | null) => {
    const isValid = newValueUTC?.isValid();

    if (!isValid || !newValueUTC) {
      Log.warnAndToast(t("errors.invalidTimeFormat"));
      return;
    }

    setValue(convertUTCToISO(newValueUTC));
  });

  useEffect(() => {
    if (error !== null) {
      setTimeout(() => {
        setError(null);
      }, UPDATED_STATION_TIME_VALIDATION_ERROR_LIFETIME);
    }
  }, [error]);

  const updateGridPagination = useEvent((rideId: number): void => {
    eventEmitter?.emit(GlobalAppEventEnum.SET_PAGINATION_TO_RIDE, rideId);
  });

  const updateStationTimes = useCallback(
    async (updatedTimeISO: TDateISO): Promise<void> => {
      if (!rideDto) {
        Log.warnAndToast(
          t("errors.invalidEntityDTOSupplied", {
            entity: t(`ride.${SINGLE_KEY}`),
          }),
        );
        return;
      }

      if (!station) {
        Log.warnAndToast(
          t("errors.invalidEntityDTOSupplied", {
            entity: t(`station.${SINGLE_KEY}`),
          }),
        );
        return;
      }

      if (!updatedTimeISO) {
        Log.debug("Empty time entered, no need to update other stations");
        return;
      }

      const arrivalDatePartComputed =
        arrivalTime || rideDto.rideStart || MIN_DATE_ISO;

      const correctedTimeISO = !isRideHasIndependentDayAdjustment
        ? adjustTimeForMidnightCrossingRide({
            newTimeISO: updatedTimeISO,
            previousTimeISO: arrivalDatePartComputed,
            rideStart: rideDto.rideStart,
            rideEnd: rideDto.rideEnd,
            timezone,
          })
        : updatedTimeISO;

      const timeOffsetMinutes = timeDiffMinutesSigned(
        dateTimeWithoutSeconds(arrivalDatePartComputed),
        dateTimeWithoutSeconds(correctedTimeISO),
      );

      Log.debug("Changing station time, time offset:", timeOffsetMinutes);

      if (!timeOffsetMinutes) {
        Log.debug("No time change defined", timeOffsetMinutes);
        return;
      }

      if (!isRideHasIndependentDayAdjustment) {
        const validateUpdatedStationTimeResult = getIsUpdatedStationTimeValid({
          rideDto,
          updatedTime: correctedTimeISO,
          station,
          timeFormat,
          timezone,
        });

        if (!validateUpdatedStationTimeResult.success) {
          setError(
            new Error(
              simpleResultErrorInfo(validateUpdatedStationTimeResult, t),
            ),
          );
          Log.errorAndToast(
            simpleResultErrorInfo(validateUpdatedStationTimeResult, t),
          );
          return;
        }
      }

      let metadataUpdated = updateMetadataStationsTime({
        rideDto,
        editStationId: station.id,
        timeShiftMinutes: timeOffsetMinutes,
        isRideHasIndependentDayAdjustment,
      });

      if (!metadataUpdated.stationsInfo?.length) {
        return;
      }

      if (isRideHasIndependentDayAdjustment) {
        Log.debug(
          "Independent day adjustment: Metadata before normalization",
          metadataUpdated,
        );

        addMissingTimesStationsInfo(metadataUpdated.stationsInfo, rideDto);

        const normalizedMetadata = normalizeRideMetadata(metadataUpdated, {
          cleanupEmptyStations: false,
          direction: rideDto.direction || DirectionEnum.PickUp,
          isRideHasIndependentDayAdjustment,
        });
        Log.debug(
          "Independent day adjustment: Normalized metadata ",
          normalizedMetadata,
        );
        metadataUpdated = normalizedMetadata;
      } else {
        const isUpdateMatchedByShiftLimits =
          isUpdatedStationTimeInsideShiftLimits({
            rideDto,
            timeOffsetMinutes,
            timeFormat,
            timezone,
            allowShiftTimeOverrideDuringManualAction,
          });

        if (!isUpdateMatchedByShiftLimits) {
          return;
        }
      }

      const { first: firstStation, last: lastStation } = getRealRouteEndpoints(
        metadataUpdated.stationsInfo,
      );

      if (!firstStation?.arrivalTime || !lastStation?.arrivalTime) {
        Log.warnAndToast(t("errors.stationsHaveNoArrivalTime"));
        return;
      }

      if (mode === RideStationPanelMode.CRUD_TAB) {
        // Edit tab: local metadata only — hub confirm runs on full ride save.
        if (isRideHasIndependentDayAdjustment) {
          updateEditingRideMetadata(rideDto.id || 0, metadataUpdated);
        } else {
          updateEditingRideMetadataRideDate({
            rideId: rideDto.id || 0,
            updatedMetadata: metadataUpdated,
          });
        }
      } else if (mode === RideStationPanelMode.GRID_PANEL) {
        // Grid panel saves immediately — confirm hub side effects before API call.
        const isHubActionConfirmed = await confirmHubRideAction({
          rides: [rideDto],
          actionKind: HubRideActionKind.Modify,
        });

        if (!isHubActionConfirmed) {
          return;
        }

        const payload: Parameters<typeof updateRideStartEndDate>[0] = {
          rideId: rideDto.id || 0,
          metadata: metadataUpdated,
          rideTypeId: rideDto?.rideTypeId,
          confirmMessageTranslationKey: "ride.UPDATE_STATION_TIME",
          isBypassConfirmation: false,
          isReturnDtoForGrid: true,
        };

        if (!isRideHasIndependentDayAdjustment) {
          payload.rideStartDate = firstStation.arrivalTime;
          payload.rideEndDate = lastStation.arrivalTime;
        }

        const updateDateResult = await updateRideStartEndDate(payload);

        if (!updateDateResult.success) {
          warnAndToastSimpleResult(updateDateResult, t);
          return;
        }

        updateGridPagination(rideDto.id || 0);
      } else {
        Log.warnAndToast(t("errors.invalidModeForStationTimeEdit"));
      }
    },
    [
      arrivalTime,
      isRideHasIndependentDayAdjustment,
      mode,
      rideDto,
      station,
      t,
      timeFormat,
      timezone,
      updateEditingRideMetadata,
      updateEditingRideMetadataRideDate,
      updateGridPagination,
      updateRideStartEndDate,
      confirmHubRideAction,
      allowShiftTimeOverrideDuringManualAction,
    ],
  );

  const onBlur = useCallback(async (): Promise<void> => {
    const isValid = value && dayjs(value).isValid();

    if (!isValid || !value) {
      Log.warnAndToast(t("errors.invalidTimeFormat"));
      return;
    }

    updateStationTimes(value);
  }, [t, updateStationTimes, value]);

  const onKeyDown = useEvent((event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.key !== KeyboardKeys.ENTER) {
      return;
    }

    onBlur();
  });

  return {
    value,
    setValue,
    error,
    setError,
    hasOverlappingDates,
    isRideHasIndependentDayAdjustment,
    timeFormat,
    timezone,
    handleTimeInputChange,
    onBlur,
    onKeyDown,
  };
};
